import { useEffect, useRef, useState } from "react";
import Typography from "../../base-components/Typography";

interface ProcessingScreenProps {
  stages: string[];
  complete: boolean;
  onDone: () => void;
  media?: React.ReactNode;
}

// Shared "AI is working" screen for the damage-detection and pricing steps.
// Progress creeps toward 92% while the real request is in flight (so the bar
// never looks stuck), then snaps to 100% and hands off once `complete` flips —
// replaces two near-identical static-spinner screens that gave no sense of
// what stage the AI pipeline was actually in.
const ProcessingScreen = ({ stages, complete, onDone, media }: ProcessingScreenProps) => {
  const [progress, setProgress] = useState(4);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (complete) return undefined;
    const id = setInterval(() => {
      setProgress((p) => (p < 92 ? p + 1 : p));
    }, 110);
    return () => clearInterval(id);
  }, [complete]);

  useEffect(() => {
    if (!complete) return undefined;
    setProgress(100);
    const t = setTimeout(() => onDoneRef.current(), 400);
    return () => clearTimeout(t);
  }, [complete]);

  const perStage = 92 / stages.length;
  const stageIndex = Math.min(
    stages.length - 1,
    Math.floor(progress / perStage)
  );

  return (
    <div className="flex flex-col items-center justify-center h-full px-6">
      {media}
      <div className="w-full max-w-xs mt-10">
        <div className="h-1.5 w-full rounded-full bg-dark-12 overflow-hidden">
          <div
            className="h-full rounded-full bg-secondary transition-[width] duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <Typography size="md" className="text-center text-black-64 mt-4">
          {stages[stageIndex]}
        </Typography>
      </div>
    </div>
  );
};

export default ProcessingScreen;
